import { UntilDestroy } from "@ngneat/until-destroy";
import { Injectable } from "@angular/core";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { CashPlusRemote } from "@modules/cashplus/infrastructure/cashplus.remote";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AccountService } from "@modules/account/services/account.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import {
	CashPlusSubscriptionsState,
	CashplusSubscriptionsStateService
} from "@modules/cashplus/+state/cashplus-subscriptions.state.service";
import {
	CashPlusConfigurationState,
	CashPlusConfigurationStateService
} from "@modules/cashplus/+state/cashplus-configurations.state.service";
import { CashPlusPortfolioStateService } from "@modules/cashplus/+state/cashplus-portfolio.state.service";
import { BannerCardTranslationValues } from "@modules/shared/banner-card-module/banner-card.component";
import { PercentSymbolPipe } from "@modules/core/pipes/percent-symbol.pipe";
import { FiatWalletsConfigurations, FundReleaseStatus, IRolloutList } from "@modules/cashplus/entities/cashplus.model";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { FiatService } from "@main-services/currency";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { CASH_PLUS_ROLL_HOUR_EURO_TIME, CASH_PLUS_ROLL_HOUR_USD_TIME } from "@helpers/constants";

export interface CashPlusStateCombined {
	configurations: CashPlusConfigurationState;
	subscriptions: CashPlusSubscriptionsState;
	isCashPlusEnabled: boolean;
}

@UntilDestroy()
@Injectable({ providedIn: "root" })
export class CashPlusFacade {
	private percentSymbolPipe: PercentSymbolPipe = new PercentSymbolPipe();

	public constructor(
		private readonly cashPlusRemote: CashPlusRemote,
		private readonly cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private readonly cashplusSubscriptionsState: CashplusSubscriptionsStateService,
		private readonly cashPlusConfigurationState: CashPlusConfigurationStateService,
		private readonly cashPlusPortfolioState: CashPlusPortfolioStateService,
		private readonly fiatService: FiatService,
		private readonly fiatWalletService: FiatWalletService,
		private readonly accountService: AccountService,
		private readonly launchDarkly: LaunchDarklyService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService
	) {}

	public isCashPlusEnabled$(): Observable<boolean> {
		return this.launchDarkly.isActive$(LD_FLAG.CASH_PLUS);
	}

	// Combine State Subscriptions + Configuration
	public getCombinedCashPlusState$(): Observable<CashPlusStateCombined> {
		// in case the FF is off, return `null` to not block execution of `combineLatest` on Portfolio
		return this.isCashPlusEnabled$().pipe(
			switchMap((enabled) => {
				if (enabled) {
					return combineLatest([this.getSubscriptionsState$(), this.getConfigurationsState$()]).pipe(
						map(([subscriptions, configurations]) => ({
							subscriptions,
							configurations,
							isCashPlusEnabled: true
						}))
					);
				}

				return of(null);
			})
		);
	}

	public fetchCombinedCashPlusState(): void {
		this.fetchApiSubscriptions();
		this.fetchApiConfigurations();
	}

	// Subscriptions State
	public getSubscriptionsState$(): Observable<CashPlusSubscriptionsState> {
		return this.cashplusSubscriptionsState.getState$().pipe(filter((state) => !!state));
	}

	public fetchApiSubscriptions(): void {
		this.isCashPlusEnabled$()
			.pipe(take(1))
			.subscribe((isCashPlusEnabled) => {
				if (isCashPlusEnabled) {
					if (
						!this.cashplusSubscriptionsState.isSubscriptionsLoaded() ||
						this.cashplusSubscriptionsState.hasError()
					) {
						this.cashPlusRemote.getSubscriptions$().subscribe({
							next: (values) => {
								this.cashplusSubscriptionsState.updateState({
									...values,
									error: false
								});
							},
							error: () => {
								this.cashplusSubscriptionsState.updateState({
									account_id: null,
									subscribed: null,
									error: true
								});
							}
						});
					}
				}
			});
	}

	public updateOptStatus$(state: string): Observable<unknown> {
		return this.cashPlusRemote.updateOptStatus$(state);
	}

	public updateSubscriptionState(subscribed: boolean): void {
		this.cashplusSubscriptionsState.updateSubscription(subscribed);
	}

	// Configuration State
	public getConfigurationsState$(): Observable<CashPlusConfigurationState> {
		return this.cashPlusConfigurationState.getState$().pipe(filter((state) => !!state));
	}

	public getConfigurationsPerFiatState$(fiatId: string): Observable<FiatWalletsConfigurations> {
		return this.getConfigurationsState$().pipe(
			filter((state) => !!state),
			map((state: CashPlusConfigurationState) => state.configurations.find((api) => api.fiat_id === fiatId))
		);
	}

	public fetchApiConfigurations(): void {
		this.isCashPlusEnabled$()
			.pipe(take(1))
			.subscribe((isCashPlusEnabled) => {
				if (isCashPlusEnabled) {
					if (
						!this.cashPlusConfigurationState.isConfigurationLoaded() ||
						this.cashPlusConfigurationState.hasError()
					) {
						this.cashPlusRemote.getConfigurations$().subscribe({
							next: (values) => {
								this.cashPlusConfigurationState.updateState({
									...values,
									error: false
								});
							},
							error: () => {
								this.cashPlusConfigurationState.updateState({
									configurations: [],
									payout_frequency: null,
									error: true
								});
							}
						});
					}
				}
			});
	}

	// We need to call all endpoints (for each fiat) to know if they have any funds (cashplus or normal)
	public fetchPortfolioFiatConfigForAllFiat(forceLoad?: boolean): void {
		this.getConfigurationsState$()
			.pipe(take(1))
			.subscribe((configs) => {
				const configPids: string[] = configs.configurations.map((c) => c.fiat_pid);

				// ForceLoad from configurations Pids, in case some API was failing previously and Pid is not included in the state
				// If we don't have portfolio state, find all fiats in configuration
				// Else fetch state unless manual force

				const loadConfigPids: boolean =
					forceLoad ||
					!this.cashPlusPortfolioState.isPortfolioLoaded() ||
					this.cashPlusPortfolioFacade.errorApi;

				if (loadConfigPids) {
					this.cashPlusPortfolioFacade.fetchPortfolioConfigForFiatPid(configPids);
				} else {
					// TODO: solve without another inner subscribe
					this.cashPlusPortfolioFacade
						.getPortfolioState$()
						.pipe(take(1))
						.subscribe((state) => {
							const statePids: string[] = state.fiatConfigs?.map((c) => c.asset_pid);
							this.cashPlusPortfolioFacade.fetchPortfolioConfigForFiatPid(statePids);
						});
				}
			});
	}

	// Utility functions
	public isDefaultWalletEligibleCashPlus$(): Observable<boolean> {
		const defaultFiatWallet = this.accountService.getDefaultFiat().getId();
		return this.isFiatIdEligibleCashPlus$(defaultFiatWallet);
	}

	public isFiatIdEligibleCashPlus$(fiatId: string): Observable<boolean> {
		return this.getConfigurationsState$().pipe(
			map((res) => res.configurations.some((conf) => conf.fiat_id === fiatId))
		);
	}

	public getFiatRollOutTime$(): Observable<IRolloutList[]> {
		return this.getConfigurationsState$().pipe(
			take(1),
			map((res) =>
				res.configurations
					.map((conf) => {
						const symbol = this.fiatService.getItemById(conf.fiat_id).getSymbol(false);

						const data: IRolloutList = {
							symbol,
							time: this.timeToNextRollout(symbol)
						};

						return data;
					})
					.sort((a, b) => parseInt(a.time) - parseInt(b.time))
			)
		);
	}

	private timeToNextRollout(symbol: string): string {
		if (symbol === "EUR" || symbol === "GBP") {
			return CASH_PLUS_ROLL_HOUR_EURO_TIME;
		}
		if (symbol === "USD") {
			return CASH_PLUS_ROLL_HOUR_USD_TIME;
		}
		return "-";
	}

	public showCashPlusDepositButtons$(fiatId: string): Observable<boolean> {
		return combineLatest([this.isCashPlusEnabled$(), this.isFiatIdEligibleCashPlus$(fiatId)]).pipe(
			map((res) => res.every((value) => value))
		);
	}

	public optOut$(): Observable<unknown> {
		return this.cashPlusRemote.optOut$();
	}

	public freeUpCash(fiatPid: string): void {
		this.cashPlusRemote.freeUpCash$(fiatPid).subscribe({
			next: (response) => {
				// @ts-ignore
				if (response.status === FundReleaseStatus.InProgress) {
					this.cashPlusPortfolioFacade.setFundReleasePending();
					this.infoService.setWarning(this.translationService.translate("cash_out_move_pending"));
				} else {
					this.cashPlusPortfolioFacade.setFundReleaseFinished();
					this.infoService.setSuccess(this.translationService.translate("cash_out_moved_balance"));
				}
				this.fiatWalletService.refetchFiatWallets();
				this.fetchCombinedCashPlusState();
				this.cashPlusPortfolioFacade.fetchPortfolioConfigForFiatPid([fiatPid]);
			},
			error: () => {
				this.infoService.setError(this.translationService.translate("cash_plus_opt_error_message"));
			}
		});
	}

	public fetchFundReleased(fiatPid: string): void {
		this.cashPlusPortfolioFacade.setFundReleaseFinished();
		this.cashPlusRemote
			.getReleased$(fiatPid)
			.pipe(
				take(1),
				catchError((err) => {
					this.cashPlusPortfolioFacade.setFundReleaseFinished();
					return of(err);
				})
			)
			.subscribe((result) => {
				if (result.status === FundReleaseStatus.InProgress) {
					this.cashPlusPortfolioState.updateState({ isFundReleasePending: true });
				}
			});
	}

	public getFiatsSymbolCashPlus$(): Observable<string[]> {
		return this.getConfigurationsState$().pipe(
			map((configurations) =>
				configurations.configurations.map((configuration) =>
					this.fiatService.getItemById(configuration.fiat_id).getSymbol()
				)
			)
		);
	}

	// Calculation for Banner APR
	public readonly yearlyYield$: Observable<number> = this.getConfigurationsState$().pipe(
		map((state: CashPlusConfigurationState) => {
			const apr: number[] = state.configurations.map(({ annual_percentage_rate }) => +annual_percentage_rate);
			state.lowestAPR = Math.min(...apr);
			return state.lowestAPR;
		})
	);

	public readonly values$: Observable<PartialBannerCardTranslationValues> = this.yearlyYield$.pipe(
		map(
			(profit) =>
				({
					headline: [this.percentSymbolPipe.transform(profit)]
				}) as PartialBannerCardTranslationValues
		)
	);

	public hasUserTransactionsInSupportedWallets$(): Observable<boolean> {
		const transactions$ = this.getConfigurationsState$().pipe(
			take(1),
			switchMap((state) =>
				// map each config to a list of transactions, then call them all at once
				forkJoin(
					state.configurations.map((c) =>
						this.cashPlusRemote.getFiatWalletTransactions(
							this.fiatWalletService.getWalletByFiatId(c.fiat_id).getId()
						)
					)
				)
			)
		);

		// return if user has any transactions in any of the wallets
		return transactions$.pipe(map((results) => results && results.flat().length > 0));
	}
}

type PartialBannerCardTranslationValues = Omit<BannerCardTranslationValues, "description" | "action">;
